import axios from 'axios'

/**
 * Estrae un messaggio d'errore leggibile da un errore di chiamata API.
 *
 * Il backend non ha un'unica shape di errore: a seconda dell'endpoint la risposta può
 * essere `{ message }`, `{ error }` (ErrorDto), `{ errors: string[] }` (ErrorsDto, es.
 * errori di validazione Identity nella creazione utente), oppure una ValidationProblemDetails
 * ASP.NET standard `{ title, errors: { campo: string[] } }` generata automaticamente dal
 * model binding (vedi Program.cs → ConfigureApiBehaviorOptions). Prima di questo fix
 * l'helper riconosceva solo `message`: su qualunque endpoint che restituisce una delle
 * altre tre shape (247 usi di ErrorDto/ErrorsDto nell'API), l'utente vedeva sempre il
 * messaggio Axios generico ("Request failed with status code 400") invece del motivo
 * reale del rifiuto (email duplicata, password troppo debole, campo obbligatorio, ecc.).
 */
export function getApiErrorMessage(err: unknown, fallback = 'Errore durante il salvataggio. Riprova.'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; error?: string; errors?: string[] | Record<string, string[]>; missingFields?: string[]; title?: string }
      | undefined

    // La pubblicazione sul sito restituisce un messaggio generico insieme a
    // `missingFields`. Mostrare anche i requisiti specifici evita che l'utente
    // debba indovinare cosa manca nella scheda immobile.
    if (Array.isArray(data?.missingFields) && data.missingFields.length > 0) {
      const heading = data.error || data.message || data.title
      const details = `Campi da completare: ${data.missingFields.join(', ')}.`
      return heading ? `${heading} ${details}` : details
    }

    // `errors` (dettaglio per-campo, es. MessageErrorsDto/ValidationProblemDetails) ha
    // priorità su `message`: quando entrambi sono presenti, `message` è quasi sempre
    // un'etichetta generica del tipo "Cambio password fallito" e `errors` contiene il
    // motivo reale (es. "La password deve contenere almeno una cifra") - mostrare prima
    // il contenitore vanificava lo scopo di avere un dettaglio specifico.
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      return data.errors.join(' ')
    }
    if (data?.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat()
      if (messages.length > 0) return messages.join(' ')
    }

    if (data?.message) return data.message
    if (data?.error) return data.error

    // ValidationProblemDetails con solo "title" (es. "Errore di validazione") e nessun
    // dettaglio per-campo utile: meglio del messaggio Axios generico, ma solo come ultima
    // risorsa prima di quello.
    if (data?.title) return data.title

    return err.message
  }
  if (err instanceof Error) {
    return err.message
  }
  return fallback
}

/**
 * True se la chiamata è stata rifiutata per permessi insufficienti (HTTP 403).
 * Il backend risponde 403 con corpo vuoto, quindi l'unico segnale affidabile
 * è lo status: serve a mostrare "tu qui non puoi accedere" invece dell'errore generico.
 */
export function isForbiddenError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 403
}

/** Messaggio standard per le sezioni/campi nascosti ai permessi dell'utente. */
export const NO_ACCESS_MESSAGE =
  'Tu qui non puoi accedere: chiedi al titolare della tua agenzia di assegnarti i permessi necessari.'
